import { CSSProperties, MouseEvent, useRef, type ReactNode } from 'react';
import styles from './Card.module.scss';
import clsx from 'clsx';

interface BaseProps {
  children?: ReactNode;
  bottom?: ReactNode;
  title: string;
  description?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  top?: ReactNode;
  style?: CSSProperties;
}

interface PropsWithHref extends BaseProps {
  href: string;
  onClickAnchor: (event: MouseEvent<HTMLAnchorElement>) => void;
}

interface PropsWithoutHref extends BaseProps {
  href?: undefined;
  onClickAnchor?: undefined;
}

// You MUST use a type alias here to create the union
export type Props = PropsWithHref | PropsWithoutHref;

export function Card({
  bottom,
  children,
  href,
  title,
  style,
  top,
  ...props
}: Props) {
  const anchorRef = useRef<HTMLAnchorElement>(null);

  function onClick(event: MouseEvent<HTMLDivElement>) {
    if (href && anchorRef.current) {
      anchorRef.current.click();
      props.onClick?.(event);
    }
  }

  return (
    <div
      className={clsx(styles['root'], styles['pressable'])}
      onClick={onClick}
      style={style}
      tabIndex={href ? 0 : undefined}
    >
      {href && (
        <a
          ref={anchorRef}
          className={styles['link']}
          href={href}
          onClick={props.onClickAnchor}
          tabIndex={-1}
        >
          {title}
        </a>
      )}
      {top && <div className={styles['top']}>{top}</div>}
      <div className={styles['title']}>{title}</div>
      {children && <div className={styles['children']}>{children}</div>}
      {bottom && <div className={styles['bottom']}>{bottom}</div>}
    </div>
  );
}
